import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Button, type SessionRole } from '../../../modules/core/index.js'
import { useFeature } from '../../../modules/add-ons/index.js'
import { useMe } from '../../../modules/accounts/index.js'
import { BUTTON_LABEL, FEATURE_KEY } from '../labels.js'
import { BriefingDialog, type BriefingContext } from './BriefingDialog.js'

/** Hidden until the client is licensed and the deployment holds a key (`ai.configured` on /auth/me); a worker never sees it. */
function useBriefingStanding(role: SessionRole): { shown: boolean; emailConfigured: boolean } {
  const me = useMe()
  const standing = useFeature(null, FEATURE_KEY)
  return { shown: role !== 'worker' && standing.licensed && (me.data?.ai?.configured ?? false), emailConfigured: me.data?.ai?.emailConfigured ?? false }
}

/**
 * The sparkle in the operator shell's header (docs/plans/ai-briefings-plan.md section 13): on a facility's
 * Dashboard it opens the choices for that facility and across facilities, on the facilities list the client's.
 * On a client page such as Insights it renders nothing - that page carries its own button.
 */
export function BriefingButton({ facilityId, onClientPage, role }: { facilityId: string | null; onClientPage: boolean; role: SessionRole }) {
  const { shown, emailConfigured } = useBriefingStanding(role)
  const [open, setOpen] = useState(false)
  if (!shown || onClientPage) {
    return null
  }
  const context: BriefingContext = facilityId ? { kind: 'facility', facilityId } : { kind: 'client' }
  return (
    <>
      <button type="button" title={BUTTON_LABEL} aria-label={BUTTON_LABEL} onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded border border-white/40 px-1.5 py-1 text-[11px] font-bold text-white align-middle hover:bg-white/10">
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {open && <BriefingDialog context={context} role={role} emailConfigured={emailConfigured} onClose={() => setOpen(false)} />}
    </>
  )
}

/** The AI summary button in the Insights page's own toolbar: the reports for this month, quarter or year, chosen in the dialog. */
export function InsightsBriefingButton({ role }: { role: SessionRole }) {
  const { shown, emailConfigured } = useBriefingStanding(role)
  const [open, setOpen] = useState(false)
  if (!shown) {
    return null
  }
  return (
    <>
      <Button icon={Sparkles} size="sm" onClick={() => setOpen(true)} title="Summarise the Insights reports with AI">
        AI summary
      </Button>
      {open && <BriefingDialog context={{ kind: 'insights' }} role={role} emailConfigured={emailConfigured} onClose={() => setOpen(false)} />}
    </>
  )
}
